import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import Api from '@/api/_legacy'
import {
  ApiAccountApprovalEvent,
  ApiBusinessUnit,
  ApiClient,
  ApiTradeRejection
} from '@/api/_legacy/generated-api'
import { UserRole, UserType } from '@/types/user'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../../../tradebuilder-v2/constants'
import { TradeActivationStep } from '../../../../tradebuilder-v2/types'
import { Errors } from '../../../../tradebuilder-v2/util'
import { parseApproveErrors } from '../../../util/validator'

export type TradeCommitmentStepState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  businessApproveSuccess?: boolean
  businessUnitName: string
  canProceed: boolean
  clientApprovalEvents: ApiAccountApprovalEvent[]
  clientApproveSuccess?: boolean
  clientName: string
  cutOffTime: string
  errors: Errors
  generalErrors?: []
  hasBusinessCompleted: boolean
  hasClientCompleted: boolean
  isConfirmedAuthorised: boolean
  isConfirmingCommit: boolean
  rejectSuccess?: boolean
  status: TradeActivationStep
  tradeDocumentationUrl: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

const initialState: TradeCommitmentStepState = {
  canProceed: true,
  errors: {},
  generalErrors: [],
  approvalEvents: [],
  clientApprovalEvents: [],
  isConfirmingCommit: false,
  isConfirmedAuthorised: false,
  accountId: undefined,
  tradeDocumentationUrl: undefined,
  status: undefined,
  userName: undefined,
  userType: undefined,
  userRoles: [],
  clientName: undefined,
  businessUnitName: undefined,
  hasBusinessCompleted: false,
  hasClientCompleted: false,
  cutOffTime: undefined
}

export type TradeCommitmentFormState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  businessUnit: ApiBusinessUnit
  client: ApiClient
  cutOffTime: string
  status: TradeActivationStep
  tradeDocumentationUrl: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export const tradeCommitmentSlice = createSlice({
  name: 'tradeCommitmentSlice',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = true
      state.hasBusinessCompleted = false
      state.hasClientCompleted = false
      state.businessApproveSuccess = undefined
      state.clientApproveSuccess = undefined
      state.errors = {}
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    initialise: (state, action: PayloadAction<TradeCommitmentFormState>) => {
      state.status = action.payload.status
      state.hasBusinessCompleted = action.payload.status > TradeActivationStep.commitment
      state.hasClientCompleted = action.payload.status > TradeActivationStep.clientCommitment
      state.accountId = action.payload.accountId
      state.tradeDocumentationUrl = action.payload.tradeDocumentationUrl
      state.approvalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(x => x.statusBefore === 'PendingCommitment')
        : []
      state.clientApprovalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(x => x.statusBefore === 'PendingClientCommitment')
        : []
      state.userName = action.payload.userName
      state.userType = action.payload.userType
      state.userRoles = action.payload.userRoles
      state.clientName = action.payload.client?.clientName
      state.businessUnitName = action.payload.businessUnit?.businessUnitName
      state.cutOffTime = action.payload.cutOffTime
      state.businessApproveSuccess = false
      state.clientApproveSuccess = false
    },
    confirmCommit: state => {
      state.isConfirmingCommit = true
    },
    closeConfirmCommit: state => {
      state.isConfirmingCommit = false
      state.isConfirmedAuthorised = false
    },
    setAuthorisedStatus: (state, action: PayloadAction<boolean>) => {
      state.isConfirmedAuthorised = action.payload
    },
    postBusinessApproveSuccess: state => {
      state.errors = {}
      state.businessApproveSuccess = true
      state.isConfirmingCommit = false
    },
    postClientApproveSuccess: state => {
      state.errors = {}
      state.clientApproveSuccess = true
      state.isConfirmingCommit = false
    },
    postTradeApproveFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
      state.isConfirmingCommit = false
    },
    postTradeRejectSuccess: state => {
      state.rejectSuccess = true
      state.errors = {}
      state.isConfirmingCommit = false
    },
    requestMoveNext: () => {
      // dummy comment to keep linter happy
    },
    moveNext: () => {
      // dummy comment to keep linter happy
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType as any, reducer)
    })
  }
})

export const postTradeReject = (accountId: string, comments: string) => async dispatch => {
  try {
    const request: ApiTradeRejection = {
      comments
    }
    await Api.tenant.rejectCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(tradeCommitmentSlice.actions.postTradeRejectSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(tradeCommitmentSlice.actions.postTradeApproveFailure(errors))
  }
}

export const actions = { ...tradeCommitmentSlice.actions }

export const postBusinessCommit = (accountId: string) => async dispatch => {
  try {
    await Api.tenant.commitCreate(accountId, TENANT_ID, { format: 'json' })
    dispatch(actions.postBusinessApproveSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

export const postClientCommit = (accountId: string) => async dispatch => {
  try {
    await Api.tenant.commitClientCreate(accountId, TENANT_ID, { format: 'json' })
    dispatch(actions.postClientApproveSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

export type TradeCommitmentActions = typeof actions

export default tradeCommitmentSlice.reducer
